/**
 * Hub — the Reading Room.
 *
 * Aesthetic: a wood-grain surface (the "desk") under a paper-tinted vignette.
 * Twelve category tiles hang like a wall of framed pictures; each carries its
 * own atmosphere — color, motif, photograph — but they all share the room.
 *
 * Layout (touch-TV contract): at landscape ≥1024px this is ONE complete
 * screenful — an editorial masthead column at the left (greeting, mode
 * toggle, the "also on the shelf" cross-links) and a height-locked 4×3
 * wall of category tiles filling the rest. Nothing below the fold.
 * Phone portrait falls back to a scrolling stack.
 *
 * No theme toggle on this surface — that lives in the global PreferencesBar
 * mounted in `App.tsx`. The room reads theme from `<html data-theme>`
 * (managed by the Preferences provider).
 *
 * Motion: `motion` library. Tile entrance is a stagger of `outBack` springs.
 * Hover lifts a tile ~6px with a tighter spring.
 */
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
/* Note: <Link> still used by category tiles below — only the BackToGames
 * exit affordance was removed in favor of the global <BackToGamesBar />. */

import { HUB_FOOTER_SLUGS } from '@/games/registry';
import { isEmbedded } from '@/host/isEmbedded';
import type { TriviaMode } from '@/games/trivia/engine';
import { AtmosphereSurface, buildSrcSet, variantOf } from '@/juice/RevealedImage';
import { usePreferences } from '@/preferences';

import { CategoryMotif } from './CategoryMotif';
import { CATEGORIES, type CategoryMeta } from './categories';
import { cardSpring, hoverSpring } from './motion';
import { sound } from './sound';

import './Hub.css';

interface ModeMeta {
  slug: TriviaMode;
  title: string;
  blurb: string;
}

const MODES: ReadonlyArray<ModeMeta> = [
  {
    slug: 'daily',
    title: 'Daily Round',
    blurb: 'Same five questions for everyone today.',
  },
  {
    slug: 'practice',
    title: 'Practice',
    blurb: 'Fresh questions whenever you want another round.',
  },
];

export function Hub() {
  const { theme } = usePreferences();

  // Mirror the theme into local state so we can pass it down without forcing
  // each tile to consume the preferences context.
  const [activeTheme, setActiveTheme] = useState(theme);
  const [selectedMode, setSelectedMode] = useState<TriviaMode>('daily');
  useEffect(() => setActiveTheme(theme), [theme]);

  // NB: we deliberately do NOT gate render on the host `init` handshake.
  // Nothing on this surface needs host data yet (resident, leaderboards, …),
  // and blocking here just broke embedded demos. See host/types.ts.

  const surfaceSrc =
    activeTheme === 'dark'
      ? '/generated/reading-room/surface-dark.png'
      : '/generated/reading-room/surface-light.png';
  const surfaceSizes = '(max-width: 430px) 100vw, 100vw';

  return (
    <div className="hub-room" data-theme={activeTheme}>
      <div className="hub-room__atmosphere" aria-hidden="true">
        <AtmosphereSurface
          src={surfaceSrc}
          avifSrc={variantOf(surfaceSrc, 'avif')}
          webpSrc={variantOf(surfaceSrc, 'webp')}
          avifSrcSet={buildSrcSet(surfaceSrc, 'avif', [430, 1024, 1366])}
          webpSrcSet={buildSrcSet(surfaceSrc, 'webp', [430, 1024, 1366])}
          sizes={surfaceSizes}
          eager
        />
      </div>
      <div className="hub-room__vignette" aria-hidden="true" />

      <aside className="hub-room__masthead">
        <header className="hub-room__header">
          <div className="hub-room__header-text">
            <p className="hub-room__eyebrow">
              <span className="hub-room__eyebrow-rule" aria-hidden="true" />
              <span>Today, in the Reading Room</span>
            </p>
            <motion.h1
              className="hub-room__title"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
            >
              Good {dayPart()}.
            </motion.h1>
            <motion.p
              className="hub-room__subtitle"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08, ease: [0.25, 1, 0.5, 1] }}
            >
              Twelve shelves of questions. Pick the one calling to you.
            </motion.p>
          </div>
        </header>

        <section className="hub-room__mode" aria-labelledby="hub-room-mode-title">
          <h2 id="hub-room-mode-title" className="sr-only">
            Trivia mode
          </h2>
          <div className="hub-room__mode-toggle" role="group" aria-label="Trivia mode">
            {MODES.map((mode) => (
              <button
                key={mode.slug}
                type="button"
                className="hub-mode"
                data-active={selectedMode === mode.slug ? 'true' : 'false'}
                onPointerDown={() => sound.unlock()}
                onClick={() => {
                  sound.play('tap-soft');
                  setSelectedMode(mode.slug);
                }}
                aria-pressed={selectedMode === mode.slug}
              >
                <span className="hub-mode__title">{mode.title}</span>
                <span className="hub-mode__blurb">{mode.blurb}</span>
              </button>
            ))}
          </div>
        </section>

        <FooterRow />
      </aside>

      <section className="hub-room__shelf" aria-labelledby="hub-room-shelf-title">
        <h2 id="hub-room-shelf-title" className="sr-only">
          Trivia categories
        </h2>
        <ul className="hub-room__tiles" role="list">
          {CATEGORIES.map((cat, i) => (
            <motion.li
              key={cat.slug}
              initial={{ opacity: 0, y: 28, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ ...cardSpring, delay: 0.12 + i * 0.04 }}
              whileHover={{ y: -6, transition: hoverSpring }}
              whileTap={{ scale: 0.985, transition: { duration: 0.08 } }}
            >
              <CategoryTile category={cat} theme={activeTheme} mode={selectedMode} />
            </motion.li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function dayPart(): string {
  if (typeof window === 'undefined') return 'afternoon';
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

// ---------------------------------------------------------------------------
// Category tile
// ---------------------------------------------------------------------------

function CategoryTile({
  category,
  theme,
  mode,
}: {
  category: CategoryMeta;
  theme: 'light' | 'dark';
  mode: TriviaMode;
}) {
  const bg = theme === 'dark' ? category.backgroundDark : category.backgroundLight;
  return (
    <Link
      to={`/play/trivia?category=${category.slug}&mode=${mode}`}
      className="cat-tile"
      data-slug={category.slug}
      data-theme={theme}
      onPointerDown={() => sound.unlock()}
      onClick={() => sound.play('tap')}
      style={
        {
          '--cat-accent': `var(${category.accentVar})`,
        } as React.CSSProperties
      }
      aria-label={`Play ${mode === 'daily' ? 'daily trivia' : 'trivia practice'} — ${category.title}`}
    >
      <div className="cat-tile__bg" aria-hidden="true">
        <AtmosphereSurface
          src={bg}
          avifSrc={variantOf(bg, 'avif')}
          webpSrc={variantOf(bg, 'webp')}
          avifSrcSet={buildSrcSet(bg, 'avif', [430, 860, 1366])}
          webpSrcSet={buildSrcSet(bg, 'webp', [430, 860, 1366])}
          sizes="(max-width: 430px) 100vw, (max-width: 1024px) 50vw, 25vw"
          eager
        />
      </div>
      <div className="cat-tile__scrim" aria-hidden="true" />
      <div className="cat-tile__motif" aria-hidden="true">
        <CategoryMotif kind={category.motifKind} />
      </div>
      {/* No per-tile mode chip: the label was identical on all twelve tiles
       * and duplicated the mode toggle in the masthead. Dropping it buys the
       * title and blurb the vertical room they need to read at a distance;
       * the mode still reaches screen readers via aria-label above. */}
      <div className="cat-tile__inner">
        <h3 className="cat-tile__title">{category.title}</h3>
        <hr className="cat-tile__divider" aria-hidden="true" />
        <p className="cat-tile__blurb">{category.blurb}</p>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Footer row — cross-links to the other games settling into the room.
// Lives at the bottom of the masthead column on landscape; after the tiles
// on the phone stack. (HUB_FOOTER_SLUGS drives the list.)
// ---------------------------------------------------------------------------

function FooterRow() {
  /* "Also on the shelf" is a row of links to OTHER games. Standalone that is
     the point of a hub; embedded it is lateral movement through a catalog the
     host did not offer — the Library gates its titles by dementia-fit rating,
     and this row does not know that. The host chose this title; it chooses the
     next one too. */
  if (isEmbedded()) return null;

  return (
    <section className="hub-room__footer-row" aria-labelledby="hub-room-coming-title">
      <h2 id="hub-room-coming-title" className="hub-room__coming-title">
        Also on the shelf
      </h2>
      <ul className="hub-room__coming" role="list">
        {HUB_FOOTER_SLUGS.map((slug) => (
          <li key={slug} className="hub-room__coming-item">
            <Link
              to={`/${slug}`}
              className="hub-room__coming-link"
              onPointerDown={() => sound.unlock()}
              onClick={() => sound.play('tap-soft')}
            >
              <span className="hub-room__coming-dot" aria-hidden="true" />
              <span>{prettyName(slug)}</span>
              <span className="hub-room__coming-arrow" aria-hidden="true">
                →
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function prettyName(slug: string): string {
  if (slug === 'word-search') return 'Word Search';
  return slug;
}
